/**
 * Logger — dev-only in all cases.
 * In release builds (__DEV__ === false) every method is a no-op so the
 * compiler/minifier can tree-shake them out entirely.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  id: string;
  level: LogLevel;
  tag: string;
  message: string;
  data?: unknown;
  timestamp: Date;
  stack?: string;
}

// In-memory ring buffer — keeps the last 200 entries during a dev session
const MAX_ENTRIES = 200;
let logBuffer: LogEntry[] = [];
let listeners: ((entries: LogEntry[]) => void)[] = [];

const LEVEL_EMOJI: Record<LogLevel, string> = {
  debug: '🔍',
  info:  'ℹ️ ',
  warn:  '⚠️ ',
  error: '🔴',
};

function emit(entry: LogEntry) {
  logBuffer = [entry, ...logBuffer].slice(0, MAX_ENTRIES);
  listeners.forEach(fn => fn([...logBuffer]));
}

function createEntry(
  level: LogLevel,
  tag: string,
  message: string,
  data?: unknown,
  error?: Error,
): LogEntry {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    level,
    tag,
    message,
    data,
    timestamp: new Date(),
    stack: error?.stack,
  };
}

// ─── Public API ─────────────────────────────────────────────────────────────

export const Logger = {
  debug(tag: string, message: string, data?: unknown) {
    if (!__DEV__) return;
    const entry = createEntry('debug', tag, message, data);
    emit(entry);
    console.debug(`${LEVEL_EMOJI.debug} [${tag}] ${message}`, data ?? '');
  },

  info(tag: string, message: string, data?: unknown) {
    if (!__DEV__) return;
    const entry = createEntry('info', tag, message, data);
    emit(entry);
    console.info(`${LEVEL_EMOJI.info} [${tag}] ${message}`, data ?? '');
  },

  warn(tag: string, message: string, data?: unknown) {
    if (!__DEV__) return;
    const entry = createEntry('warn', tag, message, data);
    emit(entry);
    console.warn(`${LEVEL_EMOJI.warn} [${tag}] ${message}`, data ?? '');
  },

  error(tag: string, message: string, error?: unknown, data?: unknown) {
    if (!__DEV__) return;
    const err = error instanceof Error ? error : undefined;
    const entry = createEntry('error', tag, message, data, err);
    emit(entry);
    console.error(`${LEVEL_EMOJI.error} [${tag}] ${message}`, error ?? '', data ?? '');
  },

  /** Log an API request/response pair */
  api(method: string, url: string, status?: number, durationMs?: number, body?: unknown) {
    if (!__DEV__) return;
    const statusIcon = !status ? '⏳' : status < 300 ? '✅' : status < 400 ? '↩️' : '❌';
    const msg = `${statusIcon} ${method.toUpperCase()} ${url}${status ? ` → ${status}` : ''}${durationMs !== undefined ? ` (${durationMs}ms)` : ''}`;
    const level: LogLevel = !status ? 'debug' : status >= 500 ? 'error' : status >= 400 ? 'warn' : 'info';
    const entry = createEntry(level, 'API', msg, body);
    emit(entry);
    console[level === 'debug' ? 'log' : level](`[API] ${msg}`, body ?? '');
  },

  /** Log a navigation event */
  nav(routeName: string, params?: unknown) {
    if (!__DEV__) return;
    Logger.debug('NAV', `→ ${routeName}`, params);
  },

  /** Subscribe to log updates (for the in-app debug panel) */
  subscribe(fn: (entries: LogEntry[]) => void): () => void {
    if (!__DEV__) return () => {};
    listeners.push(fn);
    fn([...logBuffer]);
    return () => {
      listeners = listeners.filter(l => l !== fn);
    };
  },

  /** Clear the in-memory buffer */
  clear() {
    if (!__DEV__) return;
    logBuffer = [];
    listeners.forEach(fn => fn([]));
  },

  getEntries(): LogEntry[] {
    if (!__DEV__) return [];
    return [...logBuffer];
  },
};

export type {LogEntry, LogLevel};
